import { useCallback, useEffect, useState } from 'react';
import { compact } from 'lodash';
import { Editor, Range as SlateRange, Text as SlateText } from 'slate';

import { useFreshRef } from 'src/lib/hooks/useFreshRef';

import { contentMarkupToRangeRefs } from '../lib/decorations';

const getIntersectionsWithNode = (editor, rangeRefs, nodePath) => {
  const nodeRange = Editor.range(editor, nodePath);
  const intersections = rangeRefs.map((rangeRef) => {
    if (!rangeRef.current) {
      return null;
    }
    return SlateRange.intersection(rangeRef.current, nodeRange);
  });
  return compact(intersections);
};

/**
 * `contentRevision` counts the times the editor's text was replaced from outside it. A replacement
 * rebuilds the document and the range references with it, and it lands after this effect has run —
 * so a marked-up text that arrives together with its replacement would otherwise be resolved
 * against the text going out and painted nowhere.
 */
export const useDecoratePositionedMarks = ({ editor, markup, contentRevision }, options = null) => {
  const [rangeRefs, setRangeRefs] = useState([]);
  const optionsRef = useFreshRef(options);

  useEffect(() => {
    if (!editor || !markup || markup.length === 0) {
      return;
    }

    // When "positioned marks" change, we should calculate new range references
    const newRangeRefs = contentMarkupToRangeRefs({ editor, markup }, optionsRef.current);

    // Old references should be cleared and replaced with the new ones
    setRangeRefs((prevRangeRefs) => {
      prevRangeRefs.forEach((ref) => ref.unref());
      return newRangeRefs;
    });
  }, [editor, markup, contentRevision, optionsRef]);

  return useCallback(
    ([node, path]) => {
      // We care only about "text" nodes, as we highlight only them
      if (!SlateText.isText(node)) {
        return [];
      }

      return getIntersectionsWithNode(editor, rangeRefs, path);
    },
    [editor, rangeRefs]
  );
};
